import { useEffect, useRef, useState } from "react";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import DeleteModal from "../../../../components/modals/DeleteModal";
import { useSearchParams } from "react-router-dom";
import ActionPopover from "../../../../components/popovers/actionPopover";
import TableFilter from "../../../../components/table/filter/TableFilter";
import { AiOutlinePlus } from "react-icons/ai";
import PartialSalaryModalForm from "../modals/PartialSalaryModalForm";
import PartialSalaryGroupModalForm from "../modals/PartialSalaryGroupModalForm";
import { APP_CONFIG } from "../../../../helpers/appHelpers";
import { EmployeeOrderBy } from "../../../../models/employee.dto";
import { useGetEmployee } from "../../../../services/employee.service";
import { useGetPayPeriod } from "../../../../services/pay-period.service";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import ManageTableColumns from "../../../../components/table/ManageTableColumns";
import { useColumnVisibilityModel } from "../../../../helpers/useColumnVisibilityModel";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../helpers/useManageTable";
import { PartialSalaryDto } from "../../../../models/partial-salary.dto";
import {
	useDeletePartialSalaryGroup,
	useGetPartialSalaryGroup,
} from "../../../../services/partial-salary-group.service";
import {
	useDeletePartialSalary,
	useGetPartialSalary,
} from "../../../../services/partial-salary.service";
import {
	useCheckUserPermissions,
	PermissionChecker,
	PERMISSIONS,
} from "../../../../helpers/usePermissionManager";
import {
	partialSalaryGroupColumns,
	partialSalaryGroupColumnVisibility,
	partialSalaryColumns,
	partialSalaryColumnVisibility,
} from "../tanstack-columns";
import TanStackEmployeeTable from "../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../models/table-configuration.dto";
import ExportPartialSalaryModal from "../modals/ExportPartialSalaryModal";
import { GoUpload } from "react-icons/go";
import { BsDownload } from "react-icons/bs";
import { ChevronDown, Upload } from "lucide-react";
import UploadPartialSalaries from "../modals/UploadPartialSalries";

const PartialSalaryView = () => {
	const { currentCompany } = useCompanyStore();
	const { checkUserPermissionsAnd } = useCheckUserPermissions();

	// Tab state
	const [activeTab, setActiveTab] = useState<
		"partialSalaryGroups" | "partialSalaries"
	>("partialSalaryGroups");

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openPartialSalaryGroupModal, setOpenPartialSalaryGroupModal] =
		useState(false);
	const [openPartialSalaryModal, setOpenPartialSalaryModal] = useState(false);
	const [openExportModal, setOpenExportModal] = useState(false);
	const [openUploadPartialSalaryModal, setOpenUploadPartialSalaryModal] =
		useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [isUploadExportOpen, setIsUploadExportOpen] = useState(false);
	const uploadExportDropdownRef = useRef<HTMLDivElement | null>(null);
	const [searchParams, setSearchParams] = useSearchParams();

	// Column visibility for Partial Salary Groups
	const {
		columnVisibilityModel: partialSalaryGroupColumnVisibilityModel,
		setColumnVisibilityModel: setPartialSalaryGroupColumnVisibilityModel,
	} = useColumnVisibilityModel(partialSalaryGroupColumnVisibility);

	// Column visibility for Partial Salaries
	const {
		columnVisibilityModel: partialSalaryColumnVisibilityModel,
		setColumnVisibilityModel: setPartialSalaryColumnVisibilityModel,
	} = useColumnVisibilityModel(partialSalaryColumnVisibility);

	// Partial Salary Groups table
	const {
		tableAPIRef: partialSalaryGroupTableAPIRef,
		tableData: partialSalaryGroupTableData,
		isLoading: partialSalaryGroupLoading,
		pageCount: partialSalaryGroupPageCount,
	} = useManageTableWithSearchParams<PartialSalaryDto>({
		fetchHook: (options) =>
			useGetPartialSalaryGroup(currentCompany?.id as number, options),
		options: {},
	});

	// Partial Salaries table
	const {
		tableAPIRef: partialSalaryTableAPIRef,
		tableData: partialSalaryTableData,
		isLoading: partialSalaryLoading,
		pageCount: partialSalaryPageCount,
	} = useManageTableWithSearchParams<PartialSalaryDto>({
		fetchHook: useGetPartialSalary,
		options: {
			companyId: currentCompany?.id,
		},
	});

	// Fetched data for forms
	const { data: partialSalaryGroups } = useGetPartialSalaryGroup(
		currentCompany?.id as number,
		{
			limit: APP_CONFIG.PAGE_LIMIT,
			orderBy: "createdAt:desc" as any,
		} as any,
	);
	const { data: payPeriods } = useGetPayPeriod({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: "createdAt:desc" as any,
	});
	const { data: employees } = useGetEmployee({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		status: "ACTIVE",
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});

	const handleClose = () => setAnchorEl(null);

	const handleOpenPartialSalaryGroupModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenPartialSalaryGroupModal(true);
	};

	const handleOpenPartialSalaryModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenPartialSalaryModal(true);
	};

	const handleTabChange = (tab: "partialSalaryGroups" | "partialSalaries") => {
		setActiveTab(tab);
		const updated = new URLSearchParams(searchParams);
		updated.delete("page");
		updated.delete("pageSize");
		setSearchParams(updated);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deletePartialSalaryGroup } = useDeletePartialSalaryGroup();
	const { mutate: deletePartialSalary } = useDeletePartialSalary();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);

		if (activeTab === "partialSalaryGroups") {
			deletePartialSalaryGroup(
				{ companyId: currentCompany?.id as number, id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		} else {
			deletePartialSalary(
				{ id: formDataValues?.id },
				{
					onSuccess: () => {
						reloadTableData();
						setOpenDeleteModal(false);
					},
					onSettled: () => setDeleting(false),
				},
			);
		}
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				uploadExportDropdownRef.current &&
				!uploadExportDropdownRef.current.contains(event.target as Node)
			) {
				setIsUploadExportOpen(false);
			}
		};

		if (isUploadExportOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isUploadExportOpen]);

	const handleOpenExportModal = () => {
		setIsUploadExportOpen(false);
		setOpenExportModal(true);
	};

	return (
		<>
			<div className="flex justify-between items-center mt-2 mb-3">
				<div className="flex items-center gap-3">
					<TableFilter
						fields={
							activeTab === "partialSalaryGroups"
								? [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Name", fieldName: "name" },
												{ title: "Code", fieldName: "code" },
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Start Pay Period",
											fieldName: "startPayPeriodId",
											fieldType: "SELECT_FIELD",
											options: payPeriods?.data || [],
											mapText: "code",
										},
									]
								: [
										{
											title: "",
											fieldName: "",
											fieldType: "SORT",
											options: [
												{ title: "Date Created", fieldName: "createdAt" },
												{ title: "Date Modified", fieldName: "modifiedAt" },
											],
										},
										{
											title: "Employee",
											fieldName: "employeeId",
											fieldType: "INPUT_FIELD",
											inputType: "number",
										},
										{
											title: "Start Pay Period",
											fieldName: "startPayPeriodId",
											fieldType: "SELECT_FIELD",
											options: payPeriods?.data || [],
											mapText: "code",
										},
										{
											title: "Partial Salary Group",
											fieldName: "groupId",
											fieldType: "INPUT_FIELD",
											inputType: "number",
										},
										{
											title: "Expired",
											fieldName: "expired",
											fieldType: "YES_NO",
										},
									]
						}
					/>
					<ManageTableColumns
						columns={
							activeTab === "partialSalaryGroups"
								? (partialSalaryGroupColumns as any)
								: (partialSalaryColumns as any)
						}
						defaultVisibilityModel={
							activeTab === "partialSalaryGroups"
								? partialSalaryGroupColumnVisibility
								: partialSalaryColumnVisibility
						}
						visibilityModel={
							activeTab === "partialSalaryGroups"
								? partialSalaryGroupColumnVisibilityModel
								: partialSalaryColumnVisibilityModel
						}
						setVisibilityModel={
							activeTab === "partialSalaryGroups"
								? setPartialSalaryGroupColumnVisibilityModel
								: setPartialSalaryColumnVisibilityModel
						}
						tableApiRef={
							activeTab === "partialSalaryGroups"
								? partialSalaryGroupTableAPIRef
								: partialSalaryTableAPIRef
						}
					/>
				</div>
				<div className="flex items-center gap-4 font-medium">
					{activeTab === "partialSalaryGroups" && (
						<PermissionChecker
							permissionRule="OR"
							permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}
						>
							<button
								onClick={() => setOpenUploadPartialSalaryModal(true)}
								className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
							>
								<GoUpload className="mr-2 text-[17px] text-gray-500" />
								Upload Partial Salaries
							</button>
						</PermissionChecker>
					)}

					{/* Export - Only show for Employee Allowances tab */}
					{activeTab === "partialSalaries" && (
						<button
							onClick={handleOpenExportModal}
							className="border border-gray-300 rounded-lg text-[12px] text-gray-600 py-3 px-3 flex items-center hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
						>
							<BsDownload className="mr-2 text-[18px] text-gray-500" />
							Export Partial Salary
						</button>
					)}

					<PermissionChecker
						permissionRule="OR"
						permissions={[PERMISSIONS.SALARY_WRITE]}
					>
						{activeTab === "partialSalaryGroups" ? (
							<button
								onClick={handleOpenPartialSalaryGroupModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Partial Salary Group
							</button>
						) : (
							<button
								onClick={handleOpenPartialSalaryModal}
								className="border border-gray-300 rounded-lg text-[12px] py-3 px-3 flex items-center bg-primary-mango text-white hover:bg-orange-600"
							>
								<AiOutlinePlus className="mr-2 text-lg" />
								Add Employee Partial Salary
							</button>
						)}
					</PermissionChecker>
				</div>
			</div>

			{/* Tabs */}
			<div className="flex border-b border-gray-200 mb-4">
				<button
					onClick={() => handleTabChange("partialSalaryGroups")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "partialSalaryGroups"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Partial Salary Groups
				</button>
				<button
					onClick={() => handleTabChange("partialSalaries")}
					className={`px-6 py-3 text-sm font-medium transition-colors ${
						activeTab === "partialSalaries"
							? "border-b-2 border-primary-mango text-primary-mango"
							: "text-gray-500 hover:text-gray-700"
					}`}
				>
					Employee Partial Salaries
				</button>
			</div>

			{activeTab === "partialSalaryGroups" ? (
				<TanStackEmployeeTable
					ref={partialSalaryGroupTableAPIRef}
					tableId={TableId.PARTIAL_SALARY_GROUPS}
					data={partialSalaryGroupTableData}
					columns={partialSalaryGroupColumns}
					columnVisibility={partialSalaryGroupColumnVisibilityModel}
					onColumnVisibilityChange={setPartialSalaryGroupColumnVisibilityModel}
					loading={partialSalaryGroupLoading}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.SALARY_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.currentTarget);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget);
					}}
					initialColumnVisibility={partialSalaryGroupColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([PERMISSIONS.SALARY_READ])}
					pageCount={partialSalaryGroupPageCount}
					apiRef={partialSalaryGroupTableAPIRef}
				/>
			) : (
				<TanStackEmployeeTable
					ref={partialSalaryTableAPIRef}
					tableId={TableId.PARTIAL_SALARY}
					data={partialSalaryTableData}
					columns={partialSalaryColumns}
					columnVisibility={partialSalaryColumnVisibilityModel}
					onColumnVisibilityChange={setPartialSalaryColumnVisibilityModel}
					loading={partialSalaryLoading}
					onRowClick={
						checkUserPermissionsAnd([PERMISSIONS.SALARY_WRITE])
							? (row, event) => {
									setFormData(row);
									setAnchorEl(event.currentTarget);
								}
							: undefined
					}
					onActionClick={(row, event) => {
						setFormData(row);
						setAnchorEl(event.currentTarget);
					}}
					initialColumnVisibility={partialSalaryColumnVisibility}
					pageSize={10}
					hasPermission={checkUserPermissionsAnd([PERMISSIONS.SALARY_READ])}
					pageCount={partialSalaryPageCount}
					apiRef={partialSalaryTableAPIRef}
				/>
			)}

			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={
					activeTab === "partialSalaryGroups"
						? setOpenPartialSalaryGroupModal
						: setOpenPartialSalaryModal
				}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>

			{openPartialSalaryGroupModal && (
				<PartialSalaryGroupModalForm
					open={openPartialSalaryGroupModal}
					setOpen={setOpenPartialSalaryGroupModal}
					title={
						isEditForm
							? "Update Partial Salary Group"
							: "Create Partial Salary Group"
					}
					isEditForm={isEditForm}
					tableAPIRef={partialSalaryGroupTableAPIRef}
					payPeriods={payPeriods?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openPartialSalaryModal && (
				<PartialSalaryModalForm
					open={openPartialSalaryModal}
					setOpen={setOpenPartialSalaryModal}
					title={isEditForm ? "Update Partial Salary" : "Create Partial Salary"}
					isEditForm={isEditForm}
					tableAPIRef={partialSalaryTableAPIRef}
					payPeriods={payPeriods?.data || []}
					employees={employees?.data || []}
					partialSalaryGroups={partialSalaryGroups?.data || []}
					reloadTableData={reloadTableData}
				/>
			)}
			{openUploadPartialSalaryModal && (
				<UploadPartialSalaries
					open={openUploadPartialSalaryModal}
					setOpen={setOpenUploadPartialSalaryModal}
				/>
			)}

			{openExportModal && currentCompany?.id && (
				<ExportPartialSalaryModal
					open={openExportModal}
					setOpen={setOpenExportModal}
					companyId={currentCompany.id}
				/>
			)}
		</>
	);
};

export default PartialSalaryView;
